import React from "react";
import { Mic } from "lucide-react";

import { suggestionActionLabel } from "./annotationDraft.helpers";
import { ANNOTATION_SURFACE_TOOLTIPS } from "./annotationSurface.tooltips";
import type { AnnotationSurfaceKind, AnnotationSurfaceProps } from "./annotationSurface.types";

const KIND_OPTIONS: Array<{
  kind: AnnotationSurfaceKind;
  label: string;
  tooltip: string;
}> = [
  { kind: "creative", label: "Creative", tooltip: ANNOTATION_SURFACE_TOOLTIPS.creative },
  { kind: "continuity", label: "Continuity", tooltip: ANNOTATION_SURFACE_TOOLTIPS.continuity },
  { kind: "timing", label: "Timing", tooltip: ANNOTATION_SURFACE_TOOLTIPS.timing },
  { kind: "audio", label: "Audio", tooltip: ANNOTATION_SURFACE_TOOLTIPS.audio },
  { kind: "npc", label: "NPC", tooltip: ANNOTATION_SURFACE_TOOLTIPS.npc },
  { kind: "intent", label: "Intent", tooltip: ANNOTATION_SURFACE_TOOLTIPS.intent },
  { kind: "publish", label: "Publish", tooltip: ANNOTATION_SURFACE_TOOLTIPS.publishKind },
];

const PLACEHOLDERS: Record<AnnotationSurfaceKind, string> = {
  creative: "Example: Hold on the reaction longer so the hesitation lands before the line.",
  continuity: "Example: Eyeline breaks here. Match the previous shot before the cut.",
  timing: "Example: Cut lands early. Hold two more beats before transition.",
  audio: "Example: Music swell starts too soon under dialogue. Delay the cue.",
  npc: "Example: NPC look trigger should fire here when the lead crosses frame center.",
  intent: "Example: This moment should feel uncertain, not triumphant.",
  publish: "Example: Ready for review. Confirm pacing and continuity against prior cut.",
};

const STRUCTURE_PROMPTS = ["What happened", "Why it matters", "Next action / intent"];

export default function AnnotationSurface({
  draftState,
  validationState,
  suggestionViewState,
  handoffContext,
  isDictating,
  dictationSupported,
  dictationStatus,
  interimTranscript,
  aiStatus,
  aiSuggestions,
  prefersLightMode,
  onDraftChange,
  onActionDraftChange,
  onSelectKind,
  onAskAi,
  onAcceptSuggestion,
  onTweakSuggestion,
  onMergeSuggestion,
  onDiscardSuggestion,
  onToggleDictation,
  onDropMarker,
  onPublish,
  onPublishAndClose,
  onClose,
}: AnnotationSurfaceProps): JSX.Element {
  const { draftNote, draftNextAction, draftKind } = draftState;
  const selectedKind = draftKind;
  const selectedSuggestionId = suggestionViewState?.selectedSuggestionId ?? null;
  const editingSuggestionId = suggestionViewState?.editingSuggestionId ?? null;
  const noteError = validationState.errors.draftNote;
  const selectedKindError = validationState.errors.selectedKind;
  const nextActionError = validationState.errors.draftNextAction;
  const firstValidationMessage = noteError ?? selectedKindError ?? nextActionError ?? null;
  const shellText = prefersLightMode ? "text-slate-900" : "text-white";
  const dimText = prefersLightMode ? "text-slate-600/85" : "text-cyan-50/72";
  const softBorder = prefersLightMode ? "border-slate-300/50" : "border-cyan-100/12";
  const cardFill = prefersLightMode ? "bg-slate-950/[0.06]" : "bg-slate-950/38";
  const draftFill = prefersLightMode ? "bg-white/82" : "bg-slate-950/58";
  const draftBorder = prefersLightMode ? "border-slate-300/55" : "border-cyan-200/18";
  const subtleFill = prefersLightMode ? "bg-white/68" : "bg-white/6";

  return (
    <section className={`flex flex-col gap-3 ${shellText}`} title={ANNOTATION_SURFACE_TOOLTIPS.surface} data-oc-annotation-surface="1">
      <div
        className={`flex flex-col rounded-[20px] border px-4 py-2.5 ${softBorder} ${cardFill}`}
        style={{
          boxShadow: prefersLightMode
            ? "inset 0 1px 0 rgba(255,255,255,0.6), 0 12px 26px rgba(148,163,184,0.12)"
            : "inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 26px rgba(2,12,27,0.34)",
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className={`text-[11px] font-black uppercase tracking-[0.22em] ${dimText}`}>Annotation Surface</div>
            <div className="mt-0.5 text-[13px] font-semibold leading-5">Capture a frame-accurate note without losing focus context.</div>
          </div>
        </div>

        <div className="mt-2.5 grid gap-2.5 xl:grid-cols-[minmax(0,1.35fr)_minmax(19rem,0.92fr)]">
          <div
            className={`flex flex-col rounded-[18px] border px-4 py-2.5 ${draftBorder} ${draftFill}`}
            style={{
              boxShadow: prefersLightMode
                ? "inset 0 1px 0 rgba(255,255,255,0.74), 0 10px 24px rgba(148,163,184,0.10)"
                : "inset 0 1px 0 rgba(255,255,255,0.06), 0 16px 30px rgba(2,8,23,0.28)",
            }}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${dimText}`} title={ANNOTATION_SURFACE_TOOLTIPS.draft}>
                  Annotation Draft
                </div>
                <div className={`mt-0.5 text-[12px] leading-5 ${prefersLightMode ? "text-slate-700/90" : "text-white/74"}`}>
                  Capture what changed and why it matters.
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {KIND_OPTIONS.map((option) => {
                  const isActive = option.kind === selectedKind;
                  return (
                    <button
                      key={option.kind}
                      type="button"
                      title={option.tooltip}
                      onClick={() => onSelectKind(option.kind)}
                      data-oc-annotation-kind={option.kind}
                      aria-pressed={isActive}
                      aria-label={`Select ${option.label} annotation kind`}
                      className={`rounded-full border px-3 py-1 text-[10px] font-semibold transition ${
                        isActive
                          ? prefersLightMode
                            ? "border-cyan-500/70 bg-cyan-100 text-cyan-900 shadow-[0_0_0_1px_rgba(14,165,233,0.12)]"
                            : "border-cyan-300/60 bg-cyan-300/20 text-cyan-50 shadow-[0_0_0_1px_rgba(34,211,238,0.18),0_0_18px_rgba(34,211,238,0.12)]"
                          : prefersLightMode
                            ? "border-slate-300/55 bg-white/68 text-slate-700 hover:border-cyan-400/45 hover:text-slate-900"
                            : "border-white/10 bg-white/6 text-white/72 hover:border-cyan-200/24 hover:text-white"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedKindError ? (
              <div className={`mt-2 text-[11px] font-medium ${prefersLightMode ? "text-rose-700" : "text-rose-200"}`}>
                {selectedKindError}
              </div>
            ) : null}

            {handoffContext ? (
              <div
                className={`mt-2 rounded-[16px] border px-3.5 py-2.5 ${prefersLightMode ? "border-cyan-300/55 bg-cyan-50/92 text-slate-800" : "border-cyan-300/20 bg-cyan-300/10 text-cyan-50/90"}`}
                data-oc-annotation-handoff-context="1"
              >
                <div className={`text-[10px] font-black uppercase tracking-[0.18em] ${prefersLightMode ? "text-cyan-900/85" : "text-cyan-100/82"}`}>
                  OmniStudio Draft Context
                </div>
                <div className={`mt-1 text-[12px] leading-5 ${prefersLightMode ? "text-slate-700/92" : "text-cyan-50/78"}`}>
                  Staged from OmniStudio as an editable draft. Publish and marker changes still happen only when you confirm them here.
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {handoffContext.sourceLabel ? (
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-cyan-300/60 bg-white/82 text-slate-700" : "border-cyan-200/18 bg-slate-950/32 text-cyan-50/84"}`}>
                      Source: {handoffContext.sourceLabel}
                    </span>
                  ) : null}
                  {handoffContext.sourceSceneTitle ? (
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-cyan-300/60 bg-white/82 text-slate-700" : "border-cyan-200/18 bg-slate-950/32 text-cyan-50/84"}`}>
                      Scene: {handoffContext.sourceSceneTitle}
                    </span>
                  ) : null}
                  {handoffContext.sourceNoteId ? (
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-cyan-300/60 bg-white/82 text-slate-700" : "border-cyan-200/18 bg-slate-950/32 text-cyan-50/84"}`}>
                      Note: {handoffContext.sourceNoteId}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                {STRUCTURE_PROMPTS.map((prompt) => (
                  <span
                    key={prompt}
                    className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold ${softBorder} ${prefersLightMode ? "bg-slate-100/80 text-slate-600" : "bg-white/6 text-white/54"}`}
                  >
                    {prompt}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={onAskAi}
                  data-oc-annotation-ask-ai="1"
                  aria-label="Ask AI for annotation suggestions"
                  className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-cyan-400/55 bg-cyan-50 text-cyan-900 hover:bg-cyan-100" : "border-cyan-300/24 bg-cyan-300/12 text-cyan-50 hover:bg-cyan-300/18"}`}
                >
                  Ask AI
                </button>
                <button
                  type="button"
                  title={ANNOTATION_SURFACE_TOOLTIPS.dictate}
                  onClick={onToggleDictation}
                  disabled={!dictationSupported}
                  data-oc-annotation-dictate="1"
                  aria-label={isDictating ? "Stop annotation dictation" : "Start annotation dictation"}
                  className={`inline-flex items-center justify-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold ${
                    isDictating
                      ? prefersLightMode
                        ? "border-cyan-500/60 bg-cyan-100 text-cyan-900"
                        : "border-cyan-300/55 bg-cyan-300/18 text-cyan-50"
                      : prefersLightMode
                        ? "border-slate-300/55 bg-white/70 text-slate-700"
                        : "border-white/12 bg-white/6 text-white/72"
                  } ${!dictationSupported ? "opacity-50" : ""}`}
                >
                  <Mic className="h-4 w-4" />
                  <span>{isDictating ? "Stop dictation" : "Dictate"}</span>
                </button>
              </div>
            </div>

            <textarea
              value={draftNote}
              onChange={(event) => onDraftChange(event.target.value)}
                rows={3}
              title={ANNOTATION_SURFACE_TOOLTIPS.draft}
              aria-label="Annotation draft"
              data-oc-annotation-note="1"
              placeholder={PLACEHOLDERS[selectedKind]}
              className={`mt-3 w-full resize-y rounded-[18px] border px-4 py-3 text-[14px] leading-6 outline-none transition ${
                noteError
                  ? prefersLightMode
                    ? "border-rose-300 bg-white text-slate-950 placeholder:text-slate-400 focus:border-rose-400"
                    : "border-rose-300/45 bg-slate-950/72 text-white placeholder:text-white/28 focus:border-rose-300/70"
                  : prefersLightMode
                    ? "border-slate-300/65 bg-white text-slate-950 placeholder:text-slate-400 hover:border-cyan-400/55 focus:border-cyan-500/70"
                    : "border-white/12 bg-slate-950/72 text-white placeholder:text-white/28 hover:border-cyan-300/28 focus:border-cyan-300/48"
              } min-h-[104px]`}
            />

            {noteError ? (
              <div className={`mt-2 text-[11px] font-medium ${prefersLightMode ? "text-rose-700" : "text-rose-200"}`}>
                {noteError}
              </div>
            ) : null}

            <div className={`mt-2 text-[11px] ${prefersLightMode ? "text-slate-600/90" : "text-cyan-50/66"}`}>
              {dictationSupported
                ? dictationStatus ?? "Use action: in dictation to route the next step."
                : "Browser dictation is unavailable in this session."}
            </div>

            {isDictating && interimTranscript ? (
              <div className={`mt-2 rounded-xl border px-3 py-2 text-[12px] italic ${prefersLightMode ? "border-cyan-200/60 bg-cyan-50 text-slate-700" : "border-cyan-300/18 bg-cyan-300/10 text-cyan-50/84"}`}>
                {interimTranscript}
              </div>
            ) : null}
          </div>

          <div className="grid gap-2">
            <div
              className={`rounded-[18px] border px-3.5 py-2 ${softBorder} ${subtleFill}`}
              style={{
                boxShadow: prefersLightMode
                  ? "inset 0 1px 0 rgba(255,255,255,0.56), 0 10px 22px rgba(148,163,184,0.10)"
                  : "inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 22px rgba(2,12,27,0.24)",
              }}
            >
              <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${dimText}`} title={ANNOTATION_SURFACE_TOOLTIPS.action}>
                Next Action
              </div>
                <div className={`mt-0.5 text-[11px] leading-5 ${prefersLightMode ? "text-slate-700/90" : "text-white/74"}`}>
                Follow-up, owner handoff, or editorial change to make next.
              </div>
              <input
                value={draftNextAction}
                onChange={(event) => onActionDraftChange(event.target.value)}
                title={ANNOTATION_SURFACE_TOOLTIPS.action}
                aria-label="Next action"
                data-oc-annotation-next-action="1"
                placeholder="Example: Extend reaction two beats and recheck music entry."
                className={`mt-2 w-full rounded-[16px] border px-3.5 py-2.5 text-[14px] leading-6 outline-none transition ${
                  nextActionError
                    ? prefersLightMode
                      ? "border-rose-300 bg-white text-slate-950 placeholder:text-slate-400 focus:border-rose-400"
                      : "border-rose-300/45 bg-slate-950/72 text-white placeholder:text-white/28 focus:border-rose-300/70"
                    : prefersLightMode
                      ? "border-slate-300/65 bg-white text-slate-950 placeholder:text-slate-400 hover:border-cyan-400/55 focus:border-cyan-500/70"
                      : "border-white/12 bg-slate-950/72 text-white placeholder:text-white/28 hover:border-cyan-300/28 focus:border-cyan-300/48"
                }`}
              />
              {nextActionError ? (
                <div className={`mt-2 text-[11px] font-medium ${prefersLightMode ? "text-rose-700" : "text-rose-200"}`}>
                  {nextActionError}
                </div>
              ) : null}
            </div>

            {(aiStatus || aiSuggestions.length > 0) ? (
              <div
                className={`rounded-[18px] border px-3.5 py-2 ${softBorder} ${subtleFill}`}
                style={{
                  boxShadow: prefersLightMode
                    ? "inset 0 1px 0 rgba(255,255,255,0.56), 0 10px 22px rgba(148,163,184,0.10)"
                    : "inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 22px rgba(2,12,27,0.24)",
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${dimText}`}>AI Suggestions</div>
                    <div className={`mt-0.5 text-[11px] leading-5 ${prefersLightMode ? "text-slate-700/90" : "text-white/74"}`}>
                      Review or revise suggestions before you publish.
                    </div>
                  </div>
                  {aiSuggestions.length > 0 ? (
                    <div className={`rounded-full border px-2 py-1 text-[10px] font-semibold ${softBorder} ${prefersLightMode ? "bg-white/72 text-slate-700" : "bg-white/8 text-white/76"}`}>
                      {aiSuggestions.length}
                    </div>
                  ) : null}
                </div>

                {aiStatus ? (
                  <div className={`mt-2 rounded-xl border px-3 py-2 text-[11px] ${prefersLightMode ? "border-cyan-200/60 bg-cyan-50 text-slate-700" : "border-cyan-300/18 bg-cyan-300/10 text-cyan-50/84"}`}>
                    {aiStatus}
                  </div>
                ) : null}

                {aiSuggestions.length > 0 ? (
                  <div className="mt-2 grid gap-2 md:grid-cols-2 xl:grid-cols-1">
                    {aiSuggestions.map((suggestion) => {
                      const isEditing = editingSuggestionId === suggestion.id;
                      const isSelected = selectedSuggestionId === suggestion.id;
                      const typeLabel = suggestion.type === "npc" ? "NPC" : suggestion.type.charAt(0).toUpperCase() + suggestion.type.slice(1);

                      return (
                        <div
                          key={suggestion.id}
                          data-oc-annotation-suggestion-id={suggestion.id}
                          className={`rounded-[16px] border px-3 py-2 ${isEditing || isSelected ? (prefersLightMode ? "border-cyan-400/65 bg-white" : "border-cyan-300/40 bg-cyan-300/10") : prefersLightMode ? "border-slate-300/55 bg-white/78" : "border-white/10 bg-white/5"}`}
                        >
                          <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em]">
                            <span className={`${prefersLightMode ? "text-slate-700" : "text-white/86"}`}>{typeLabel}</span>
                            {suggestion.severity ? <span className={`${prefersLightMode ? "text-cyan-800" : "text-cyan-100/88"}`}>{suggestion.severity}</span> : null}
                            <span className={`${dimText}`}>{suggestion.anchorTimecode}</span>
                            {suggestion.subtype ? <span className={`${dimText}`}>{suggestion.subtype}</span> : null}
                            {isEditing ? <span className={`${prefersLightMode ? "text-cyan-700" : "text-cyan-200"}`}>Editing</span> : null}
                            {!isEditing && isSelected ? <span className={`${prefersLightMode ? "text-cyan-700" : "text-cyan-200"}`}>Selected</span> : null}
                          </div>
                          <div className="mt-1 text-[12px] font-semibold leading-5">{suggestion.summary ?? suggestion.rawNote}</div>
                          {suggestionActionLabel(suggestion) ? (
                            <div className={`mt-1 text-[11px] leading-5 ${prefersLightMode ? "text-slate-600" : "text-white/66"}`}>
                              Next step: {suggestionActionLabel(suggestion)}
                            </div>
                          ) : null}
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => onAcceptSuggestion(suggestion.id)}
                              data-oc-annotation-suggestion-action="accept"
                              aria-label={`Accept AI suggestion ${suggestion.id}`}
                              className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-cyan-400/55 bg-cyan-50 text-cyan-900 hover:bg-cyan-100" : "border-cyan-300/24 bg-cyan-300/12 text-cyan-50 hover:bg-cyan-300/18"}`}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              onClick={() => onTweakSuggestion(suggestion.id)}
                              data-oc-annotation-suggestion-action="tweak"
                              aria-label={`Tweak AI suggestion ${suggestion.id}`}
                              className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-slate-300/55 bg-white/75 text-slate-700 hover:bg-white" : "border-white/10 bg-white/6 text-white/76 hover:bg-white/10"}`}
                            >
                              Tweak
                            </button>
                            <button
                              type="button"
                              onClick={() => onMergeSuggestion(suggestion.id)}
                              data-oc-annotation-suggestion-action="merge"
                              aria-label={`Merge AI suggestion ${suggestion.id}`}
                              className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-slate-300/55 bg-white/75 text-slate-700 hover:bg-white" : "border-white/10 bg-white/6 text-white/76 hover:bg-white/10"}`}
                            >
                              Merge
                            </button>
                            <button
                              type="button"
                              onClick={() => onDiscardSuggestion(suggestion.id)}
                              data-oc-annotation-suggestion-action="discard"
                              aria-label={`Discard AI suggestion ${suggestion.id}`}
                              className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${prefersLightMode ? "border-rose-300/60 bg-rose-50 text-rose-700 hover:bg-rose-100" : "border-rose-300/24 bg-rose-300/10 text-rose-100 hover:bg-rose-300/16"}`}
                            >
                              Discard
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div
              className={`rounded-[18px] border px-3.5 py-2 ${softBorder} ${subtleFill}`}
              style={{
                boxShadow: prefersLightMode
                  ? "inset 0 1px 0 rgba(255,255,255,0.56), 0 10px 22px rgba(148,163,184,0.10)"
                  : "inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 22px rgba(2,12,27,0.24)",
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${dimText}`}>Actions</div>
                <button
                  type="button"
                  title={ANNOTATION_SURFACE_TOOLTIPS.close}
                  onClick={onClose}
                  data-oc-annotation-close="1"
                  aria-label="Close annotation surface"
                  className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${softBorder} ${prefersLightMode ? "bg-white/65 text-slate-700 hover:bg-white" : "bg-white/8 text-white/72 hover:bg-white/12"}`}
                >
                  Close
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 xl:flex-nowrap">
                <button
                  type="button"
                  title={ANNOTATION_SURFACE_TOOLTIPS.dropMarker}
                  onClick={onDropMarker}
                  data-oc-annotation-drop-marker="1"
                  aria-label="Drop annotation marker"
                  className={`inline-flex min-w-[8.5rem] flex-1 items-center justify-center rounded-full border px-3.5 py-2 text-[12px] font-semibold ${
                    prefersLightMode
                      ? "border-cyan-300/70 bg-cyan-50 text-cyan-900 hover:bg-cyan-100"
                      : "border-cyan-300/24 bg-cyan-300/12 text-cyan-50 hover:bg-cyan-300/18"
                  }`}
                >
                  Drop Marker
                </button>
                <button
                  type="button"
                  title={ANNOTATION_SURFACE_TOOLTIPS.publishAnnotation}
                  onClick={onPublish}
                  disabled={!validationState.canPublish}
                  data-oc-annotation-publish="1"
                  aria-label="Publish annotation"
                  className={`inline-flex min-w-[8.5rem] flex-1 items-center justify-center rounded-full border px-3.5 py-2 text-[12px] font-semibold ${
                    prefersLightMode
                      ? "border-cyan-300/70 bg-cyan-50 text-cyan-900 hover:bg-cyan-100"
                      : "border-cyan-300/24 bg-cyan-300/12 text-cyan-50 hover:bg-cyan-300/18"
                  } ${!validationState.canPublish ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  Publish
                </button>
                <button
                  type="button"
                  title={ANNOTATION_SURFACE_TOOLTIPS.publishAndClose}
                  onClick={onPublishAndClose}
                  disabled={!validationState.canPublish}
                  data-oc-annotation-publish-close="1"
                  aria-label="Publish annotation and close surface"
                  className={`inline-flex min-w-[10rem] flex-[1.15] items-center justify-center rounded-full border px-4 py-2 text-[12px] font-semibold ${
                    prefersLightMode
                      ? "border-cyan-500/70 bg-cyan-400 text-cyan-950 shadow-[0_12px_28px_rgba(14,165,233,0.22)] hover:bg-cyan-300"
                      : "border-cyan-300/70 bg-cyan-300/78 text-slate-950 shadow-[0_0_0_1px_rgba(34,211,238,0.2),0_0_24px_rgba(34,211,238,0.18)] hover:bg-cyan-200"
                  } ${!validationState.canPublish ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  Publish and Close
                </button>
              </div>
              {!validationState.canPublish && firstValidationMessage ? (
                <div className={`mt-2 text-[11px] font-medium ${prefersLightMode ? "text-rose-700" : "text-rose-200"}`}>
                  {firstValidationMessage}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}